import type { ClientTool } from "@/services/agentChat";
import { getScreenContext } from "./screenContext";
import { captureScreenContextAfterSettle } from "./screenTools";

/** Routes the agent may navigate to; every other target is rejected (#675). */
export const AGENT_NAVIGABLE_ROUTES = [
  "/markets",
  "/watchlists",
  "/exports",
  "/stats",
  "/profile",
  "/firms",
] as const;

const navigableRoutes = new Set<string>(AGENT_NAVIGABLE_ROUTES);

export type NavigateFn = (to: string) => Promise<void> | void;

/**
 * The `navigate_to` client tool: switches the app to another screen through the
 * router (the same path a nav click takes) and returns the post-navigation
 * screen context so the model knows where the user landed.
 */
export function createNavigateTool(navigate: NavigateFn): ClientTool {
  return {
    name: "navigate_to",
    description:
      "Navigate the user's browser to another Tower screen. Only these routes are allowed: "
      + `${AGENT_NAVIGABLE_ROUTES.join(", ")}. Returns the screen context after navigating.`,
    parameters: {
      type: "object",
      properties: {
        route: { type: "string", enum: [...AGENT_NAVIGABLE_ROUTES] },
      },
      required: ["route"],
      additionalProperties: false,
    },
    async execute(args) {
      const route = args.route;
      if (typeof route !== "string" || !navigableRoutes.has(route)) {
        return {
          error: `Route ${JSON.stringify(route)} is not allowed. Allowed routes: ${AGENT_NAVIGABLE_ROUTES.join(", ")}.`,
        };
      }

      const before = getScreenContext(window.location.pathname, window.location.search);
      await navigate(route);
      return { context: await captureScreenContextAfterSettle(before) };
    },
  };
}
